import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { InvoicePaymentStatus, PaymentMode, PaymentStatusForMessage } from '@core/models/api-response.model';
import { PAYMENT_MODE } from './constant/shared.constant';
import { DateTime } from 'luxon';

@Pipe({
  name: 'customDate'
})
export class CustomDatePipe implements PipeTransform {

  transform(value: any): string {
    if (!value) return '';
    
    const date = new Date(value);
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();

    const months = [
      'January', 'February', 'March', 'April', 'May', 'June', 'July',
      'August', 'September', 'October', 'November', 'December'
    ];

    const month = months[monthIndex];
    const suffix = this.getOrdinalSuffix(day);

    return `${day}${suffix} ${month}, ${year}`;
  }

  // Function to get ordinal suffix for day (e.g., "st", "nd", "rd", "th")
  private getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:  return 'st';
      case 2:  return 'nd';
      case 3:  return 'rd';
      default: return 'th';
    }
  }
}

@Pipe({
  name: 'customDateFinancial'
})
export class CustomDatePipeFinancial implements PipeTransform {
  transform(value: any): any {
    const datePipe = new DatePipe('en-US');
    const formattedDate = datePipe.transform(value, 'MM.dd.yyyy - HH:mm');
    return formattedDate;
  }
}

@Pipe({
  name: 'customDate2'
})
export class customDate2 extends DatePipe implements PipeTransform {
  override transform(value: any): any {
    if (value) {
      const dateValue = new Date(value);

      // Format date to 'MMM dd, yyyy' (e.g., 'Oct 16, 2023')
      const formattedDate = super.transform(dateValue, 'MMM dd, yyyy');

      // Format time to 'hh.mm' (e.g., '01.00')
      const formattedTime = super.transform(dateValue, 'HH:mm');

      return `${formattedDate} - ${formattedTime}`;
    }
    return null;
  }
}

@Pipe({
  name: 'customDate1'
})
export class customDate1 extends DatePipe implements PipeTransform {
  override transform(value: any): any {
    if (value) {
      const dateValue = new Date(value);

      // Format date to 'MMM dd, yyyy' (e.g., 'Oct 16, 2023')
      const formattedDate = super.transform(dateValue, 'MMM dd, yyyy');

      return formattedDate;
    }
    return null;
  }
}

@Pipe({
  name: 'removeDecimal'
})
export class RemoveDecimalPipe implements PipeTransform {
  transform(value: any): any {
    if (typeof value === 'string' || typeof value === 'number') {
      const num = parseFloat(value as string); // Convert to number
      return num % 1 === 0 
        ? num.toString() // If no decimal part, return as is
        : num.toFixed(2); // Round to two decimal places
    }
    return value; // Return original value if not a number
  }
}

 // offline and online mode string for grid
@Pipe({
  name: 'paymentModeValue'
})
export class PaymentModeValuePipe implements PipeTransform {
  transform(paymentMode: PaymentMode): string {
    switch (paymentMode) {
      case PaymentMode.Online:
        return PAYMENT_MODE.ONLINE;
      case PaymentMode.Offline:
        return PAYMENT_MODE.OFFLINE;
      default:
        return '_';
    }
  }}

// common class for payment status 
@Pipe({
  name: 'PaymentStatusClass'
})
export class PaymentStatusClassPipe implements PipeTransform {
  transform(paymentStatus: PaymentStatusForMessage) {
    switch (paymentStatus) {
      case PaymentStatusForMessage.Processing:
        return 'pstatus-in-progress';
      case PaymentStatusForMessage.Failure:
      case PaymentStatusForMessage.DisputeCreated:
      case PaymentStatusForMessage.DisputeUpdated:
      case PaymentStatusForMessage.DisputeLoss:
      case PaymentStatusForMessage.DisputeExpired:
        return 'pstatus-payment-failed';
      case PaymentStatusForMessage.Cancelled:
        return 'pstatus-cancel';
      case PaymentStatusForMessage.Completed:
      case PaymentStatusForMessage.PartialCancel:
        case PaymentStatusForMessage.RecurringPendingPayment:
        return 'pstatus-paid';
      case PaymentStatusForMessage.PaymentLinkExpire:
      case PaymentStatusForMessage.Pending:
      case PaymentStatusForMessage.PendingWithOffline:
      case PaymentStatusForMessage.InvoiceGenerated:
      case PaymentStatusForMessage.CheckoutUrlGenrated:
        return 'pstatus-pending';
      default:
        
        return '';
    }
  }
}
// common class for invoice and custom invoice payment status
@Pipe({
  name: 'invoicePaymentStatus'
})
export class invoicePaymentStatusPipe implements PipeTransform {
  transform(paymentStatus: InvoicePaymentStatus) {

    switch (paymentStatus) {
      case InvoicePaymentStatus.InProgress:
        return 'pstatus-in-progress';
      case InvoicePaymentStatus.PaymentFailed:
        return 'pstatus-payment-failed';
      case InvoicePaymentStatus.Cancel:
        return 'pstatus-cancel';
      case InvoicePaymentStatus.Paid:
        return 'pstatus-paid';
      case InvoicePaymentStatus.Pending:
      case InvoicePaymentStatus.CheckoutUrlGenrated:
        return 'pstatus-pending';
      default:
        return '';
    }
  }
}

// common booking status color code class with border
@Pipe({
  name: 'BookingStatusClass'
})
export class BookingStatusClassPipe implements PipeTransform {
  transform(status: string) {
    switch (status) {
      case 'Pending':
        return 'common-pending';
      case 'Partially Cancelled':
        return 'common-partial';
      case 'In Progress':
        return 'common-in-progress';
      case 'Completed':
        return 'common-completed';
      case 'Cancelled':
        return 'common-cancel';
      case 'Payment Failed':
        return 'booking-status-payment-failed';
      case 'Upcoming':
        return 'common-upcoming';
      default:
        return '';
    }
  }
}

// common booking status color code class without border
@Pipe({
  name: 'BookingStatusClassWithoutBorder'
})
export class BookingStatusClassWithoutBorderPipe implements PipeTransform {
  transform(status: string) {
    switch (status) {
      case 'Upcoming':
      case 'In Progress':
        return 'pstatus-in-progress';
      case 'Completed':
        return 'pstatus-completed';
      case 'Pending':
        return 'pstatus-pending';
      case 'Cancelled':
        return 'pstatus-cancel';
      case 'Partially Cancelled':
        return 'pstatus-Partial';
      default:
        return '';
    }

  }
}

@Pipe({ name: 'luxonFormat' })
export class LuxonFormatPipe implements PipeTransform {
  transform(value: string, format: string = 'MMM d, yyyy - HH:mm'): string {
    const date = DateTime.fromISO(value, { setZone: true });
    return date.toFormat(format);
  }
}

